import { Agent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { AT_EXPLORE_ENDPOINTS, MACKINAC_XRPC_URL } from '@/constants/api'
import { getServiceAuthToken } from '@/services/mackinacClient'

const GET_SUBSCRIPTIONS_NSID = 'app.mackinac.subscriptions.getSubscriptions'

export interface SubscriptionItem {
    uri: string
    rkey: string
    publicationUri: string
    publicationName?: string
    publicationAvatarUrl?: string
}

function isSubscriptionItem(item: unknown): item is SubscriptionItem {
    return (
        typeof item === 'object' &&
        item !== null &&
        typeof (item as Record<string, unknown>).uri === 'string' &&
        typeof (item as Record<string, unknown>).rkey === 'string' &&
        typeof (item as Record<string, unknown>).publicationUri === 'string'
    )
}

export interface FetchUserSubscriptionsOpts {
    limit?: number
    cursor?: string
}

export interface FetchUserSubscriptionsResult {
    subscriptions: SubscriptionItem[]
    cursor?: string
}

export async function fetchUserSubscriptions(
    session: OAuthSession,
    opts?: FetchUserSubscriptionsOpts
): Promise<FetchUserSubscriptionsResult> {
    try {
        const agent = new Agent(session)
        const token = await getServiceAuthToken(agent, GET_SUBSCRIPTIONS_NSID)
        const baseUrl = `${MACKINAC_XRPC_URL}/xrpc/${GET_SUBSCRIPTIONS_NSID}`
        const params = new URLSearchParams()
        if (opts?.limit !== undefined) params.set('limit', String(opts.limit))
        if (opts?.cursor) params.set('cursor', opts.cursor)
        const query = params.toString()
        const response = await fetch(query ? `${baseUrl}?${query}` : baseUrl, {
            headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) return { subscriptions: [] }
        const body = (await response.json()) as { subscriptions?: unknown[]; cursor?: string }
        if (!Array.isArray(body.subscriptions)) return { subscriptions: [] }
        return {
            subscriptions: body.subscriptions.filter(isSubscriptionItem),
            cursor: body.cursor,
        }
    } catch {
        return { subscriptions: [] }
    }
}

/**
 * Fetches the viewer's complete subscription record set by paginating
 * `fetchUserSubscriptions` to exhaustion, rather than relying on its
 * server-side default page size (25). Without this, a viewer with more than
 * one page of subscriptions has some invisible to the Subscribe button,
 * which then renders "Subscribe" for an already-subscribed publication —
 * clicking it creates a duplicate PDS record (mackinac#2603).
 */
export async function fetchAllUserSubscriptions(
    session: OAuthSession
): Promise<FetchUserSubscriptionsResult> {
    const subscriptions: SubscriptionItem[] = []
    let cursor: string | undefined
    do {
        const page = await fetchUserSubscriptions(session, { cursor })
        subscriptions.push(...page.subscriptions)
        cursor = page.cursor
    } while (cursor)
    return { subscriptions }
}

interface SubscriptionRecord {
    uri: string
    cid: string
    value: {
        $type: string
        publication?: string
    }
}

interface SubscriptionCollectionResponse {
    records: SubscriptionRecord[]
    cursor?: string
}

/**
 * Extracts the DID from an AT-URI of the form:
 *   at://did:plc:xxx/site.standard.publication/rkey
 * Returns null if the URI doesn't match the expected format.
 */
export function extractDidFromAtUri(atUri: string): string | null {
    const match = atUri.match(/^at:\/\/(did:[^/]+)\//)
    return match ? match[1] : null
}

/**
 * Fetches the user's site.standard.graph.subscription records and returns
 * the unique list of author DIDs extracted from each publication AT-URI.
 */
export async function fetchSubscriptions(did: string): Promise<string[]> {
    const url = AT_EXPLORE_ENDPOINTS.atCollection(did, 'site.standard.graph.subscription')
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(`Failed to fetch subscriptions: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as SubscriptionCollectionResponse
    const records = Array.isArray(data.records) ? data.records : []

    const dids = new Set<string>()
    for (const record of records) {
        const publication = record.value?.publication
        if (!publication) continue
        const authorDid = extractDidFromAtUri(publication)
        if (authorDid) dids.add(authorDid)
    }

    return Array.from(dids)
}

/**
 * Creates a new site.standard.graph.subscription record pointing to the given
 * publication AT-URI.
 */
/**
 * Checks the viewer's own PDS directly — not the Mongo-backed status query,
 * which can lag or be paginated away — for a subscription record that
 * already points at `publicationUri`. Used as a last-line guard against
 * writing a duplicate record when the caller's cached status was stale or
 * wrong (mackinac#2603).
 */
async function findExistingSubscriptionRecord(
    agent: Agent,
    did: string,
    publicationUri: string
): Promise<{ uri: string; rkey: string } | null> {
    let cursor: string | undefined
    do {
        const response = await agent.com.atproto.repo.listRecords({
            repo: did,
            collection: 'site.standard.graph.subscription',
            limit: 100,
            cursor,
        })
        for (const record of response.data.records) {
            const value = record.value as { publication?: string }
            if (value.publication === publicationUri) {
                const rkey = record.uri.split('/').at(-1)
                if (rkey) return { uri: record.uri, rkey }
            }
        }
        cursor = response.data.cursor ?? undefined
    } while (cursor)
    return null
}

/**
 * Lists every existing subscription record matching `publicationUri`, across
 * all pages — unlike `findExistingSubscriptionRecord` (which returns as soon
 * as it finds one match, for the cheap pre-create check), this always walks
 * every page so a post-create self-heal can find every duplicate a race
 * window let through.
 */
async function findAllMatchingSubscriptionRecords(
    agent: Agent,
    did: string,
    publicationUri: string
): Promise<{ uri: string; rkey: string }[]> {
    const matches: { uri: string; rkey: string }[] = []
    let cursor: string | undefined
    do {
        const response = await agent.com.atproto.repo.listRecords({
            repo: did,
            collection: 'site.standard.graph.subscription',
            limit: 100,
            cursor,
        })
        for (const record of response.data.records) {
            const value = record.value as { publication?: string }
            if (value.publication === publicationUri) {
                const rkey = record.uri.split('/').at(-1)
                if (rkey) matches.push({ uri: record.uri, rkey })
            }
        }
        cursor = response.data.cursor ?? undefined
    } while (cursor)
    return matches
}

export async function createSubscription(
    session: OAuthSession,
    publicationUri: string
): Promise<{ uri: string; rkey: string }> {
    // Validate that publicationUri is a well-formed AT-URI before writing to PDS
    const atUriRe = /^at:\/\/(did:[^/]+)\/[^/]+\/[^/]+$/
    if (!atUriRe.test(publicationUri)) {
        throw new Error(`Invalid publication URI: ${publicationUri}`)
    }
    const agent = new Agent(session)

    const existing = await findExistingSubscriptionRecord(agent, session.did, publicationUri)
    if (existing) return existing

    const response = await agent.com.atproto.repo.createRecord({
        repo: session.did,
        collection: 'site.standard.graph.subscription',
        record: {
            $type: 'site.standard.graph.subscription',
            publication: publicationUri,
            createdAt: new Date().toISOString(),
        },
    })

    const rkey = response.data.uri.split('/').at(-1)
    if (!rkey) throw new Error(`Malformed AT URI from createRecord: ${response.data.uri}`)

    // Self-heal against the check-then-create race above: the pre-create
    // check and this write are two separate PDS calls, so two
    // near-simultaneous createSubscription calls (two open tabs, or web and
    // mobile racing) can both observe "no existing record" and both create
    // one — reproducing the exact duplicate-record bug this PR exists to fix
    // (mackinac#2603). Re-list every matching record here and drop all but
    // the earliest so a race that actually landed self-corrects within this
    // same request, instead of leaving a duplicate to linger indefinitely.
    // Best-effort: if the re-list or cleanup itself fails, fall back to the
    // record just created rather than failing an otherwise-successful
    // subscribe.
    try {
        const allMatches = await findAllMatchingSubscriptionRecords(
            agent,
            session.did,
            publicationUri
        )
        if (allMatches.length > 1) {
            const [keep, ...duplicates] = [...allMatches].sort((a, b) =>
                a.rkey < b.rkey ? -1 : a.rkey > b.rkey ? 1 : 0
            )
            await Promise.allSettled(
                duplicates.map(dup =>
                    agent.com.atproto.repo.deleteRecord({
                        repo: session.did,
                        collection: 'site.standard.graph.subscription',
                        rkey: dup.rkey,
                    })
                )
            )
            return keep
        }
    } catch {
        // fall through to the record just created
    }

    return { uri: response.data.uri, rkey }
}

/**
 * Deletes a site.standard.graph.subscription record by rkey.
 */
export async function deleteSubscription(session: OAuthSession, rkey: string): Promise<void> {
    // Validate rkey is a safe alphanumeric TID before sending to PDS
    if (!/^[a-z2-7]{13}$/.test(rkey)) {
        throw new Error(`Invalid subscription rkey: ${rkey}`)
    }
    const agent = new Agent(session)
    await agent.com.atproto.repo.deleteRecord({
        repo: session.did,
        collection: 'site.standard.graph.subscription',
        rkey,
    })
}
